import React, { useCallback } from 'react';
import { Box, Paper, IconButton, Tooltip, Typography } from '@mui/material';
import { DragIndicator as DragIcon, Add as AddIcon } from '@mui/icons-material';
import { Droppable, Draggable } from 'react-beautiful-dnd';

const InlineRowLayout = ({
  element,
  updateElement,
  renderInlineEditableElement,
  isInZone = false,
  layoutId = null,
  zoneId = null,
  handleDragOver = null,
  handleDragLeave = null,
  handleDrop = null,
  isBrandsPage = false
}) => {
  console.log('🎯 InlineRowLayout props:', {
    elementId: element?.id,
    hasDragOver: !!handleDragOver,
    hasDragLeave: !!handleDragLeave,
    hasDrop: !!handleDrop
  });


  const addElementToInlineRow = useCallback((elementType) => {
    console.log('➕ Adding element to inline row:', elementType);

    const newElement = {
      id: `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: elementType,
      content: elementType === 'text' ? 'Add your compelling offer description here. Explain the unique value proposition that makes this offer irresistible.' : `New ${elementType}`,
      styles: {
        position: 'relative',
        display: 'inline-block',
        margin: '0 5px',
        maxWidth: '300px',
        wordWrap: 'break-word'
      }
    };

    const updatedZones = element.zones.map(zone => {
      if (zone.id === element.zones[0].id) {
        return {
          ...zone,
          elements: [...zone.elements, newElement]
        };
      }
      return zone;
    });

    console.log('🔄 Updated zones for inline row:', updatedZones);

    updateElement(element.id, {
      zones: updatedZones
    }, isInZone, layoutId, zoneId);
  }, [element, updateElement, isInZone, layoutId, zoneId]);

  return (
    <Box
      data-element-id={element.id}
      sx={{
        backgroundColor: element.styles?.backgroundColor || 'transparent',
        border: element.styles?.border || '1px dashed #ccc',
        borderRadius: element.styles?.borderRadius || 1,
        p: element.styles?.padding || 1,
        margin: element.styles?.margin || '0 0 16px 0',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 }
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ fontWeight: 'bold', color: '#495057' }}>
          Inline Row Layout
        </span>
        {isBrandsPage && (
          <Tooltip title="Add element to this row">
            <IconButton
              size="small"
              onClick={() => addElementToInlineRow('text')}
              sx={{ ml: 'auto' }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Zone Info - Moved to top */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Inline Zone ({element.zones[0].elements.length} items)
      </Typography>

      <Droppable droppableId={`zone-${element.id}-${element.zones[0].id}`} direction="horizontal">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '20px',
              minHeight: '80px',
              padding: '15px',
              backgroundColor: snapshot.isDraggingOver ? '#e3f2fd' : '#fafafa',
              borderRadius: '4px',
              border: '2px solid #1976d2',
              textAlign: 'left',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
              overflow: 'visible',
              '& > div': {
                flexShrink: 0,
                maxWidth: '300px'
              }
            }}
            onDragOver={(e) => {
              console.log('🎯 InlineRow onDragOver triggered', { layoutId: element.id, zoneId: element.zones[0].id });
              if (handleDragOver) {
                handleDragOver(e, { layoutId: element.id, zoneId: element.zones[0].id });
              }
            }}
            onDragLeave={(e) => {
              console.log('🎯 InlineRow onDragLeave triggered');
              if (handleDragLeave) {
                handleDragLeave();
              }
            }}
            onDrop={(e) => {
              console.log('🎯 InlineRow onDrop triggered', { elementId: element.id, zoneId: element.zones[0].id });
              if (handleDrop) {
                handleDrop(e, element.id, element.zones[0].id);
              }
            }}
          >
            {element.zones[0].elements.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', width: '100%', textAlign: 'center' }}>
                Drop elements here
              </Typography>
            )}
            {element.zones[0].elements.length > 0 && (
              element.zones[0].elements.map((zoneElement, index) => (
                <Draggable
                  key={zoneElement.id}
                  draggableId={zoneElement.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                        opacity: snapshot.isDragging ? 0.5 : 1,
                        transform: snapshot.isDragging
                          ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                          : provided.draggableProps.style?.transform,
                        zIndex: snapshot.isDragging ? 1000 : 1,
                        margin: '0 5px',
                        minWidth: 'fit-content',
                        maxWidth: '300px',
                        flexShrink: 0,
                        alignSelf: 'flex-start'
                      }}

                    >
                      {renderInlineEditableElement(zoneElement, true, element.id, element.zones[0].id, index, provided.dragHandleProps)}
                    </div>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Box>
  );
};

export default InlineRowLayout; 